import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { Loader2, PanelRightOpen } from "lucide-react";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useProjectContext } from "~/components/layout/project-context";
import { ScreeningEmptyState } from "~/components/screening/screening-empty-state";
import { ExclusionReasonDialog } from "~/components/screening/exclusion-reason-dialog";
import { ScreeningActions } from "~/components/screening/screening-actions";
import { ScreeningCitationCard } from "~/components/screening/screening-citation-card";
import { ScreeningHeader } from "~/components/screening/screening-header";
import {
  ScreeningSidebar,
  type ReviewDecision,
  type ReviewedCitationItem,
  type ScreeningWorkbenchTab,
} from "~/components/screening/screening-sidebar";
import { TagNotesStrip } from "~/components/screening/tag-notes-strip";
import { Button } from "~/components/ui/button";
import { ScrollArea } from "~/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "~/components/ui/sheet";
import {
  $submitDualScreeningDecision,
  $undoDualScreeningDecision,
  type DualScreeningQueueCitation,
} from "~/lib/screening/dual-screening";
import {
  $submitScreeningDecision,
  $undoScreeningDecision,
  type ScreeningQueueCitation,
} from "~/lib/screening/functions";
import {
  citationNotesQueryOptions,
  citationTagsQueryOptions,
  dualScreeningQueueQueryOptions,
  dualScreeningReviewedHistoryQueryOptions,
  dualScreeningStatsQueryOptions,
  exclusionCodesQueryOptions,
  projectTagsQueryOptions,
  screeningQueueQueryOptions,
  screeningReviewedHistoryQueryOptions,
  screeningSettingsQueryOptions,
  screeningStatsQueryOptions,
  suggestedTagsQueryOptions,
} from "~/lib/screening/queries";
import type { CitationTag } from "~/lib/screening/tags";

const MAX_UNDO_STACK = 10;
const MAX_RECENT_REVIEWED = 20;

type QueueCitation = ScreeningQueueCitation | DualScreeningQueueCitation;

interface UndoEntry {
  citation: QueueCitation;
  decision: "included" | "excluded" | "maybe" | "include" | "exclude";
}

function normalizeDecision(decision: UndoEntry["decision"] | "maybe"): ReviewDecision {
  if (decision === "included") return "include";
  if (decision === "excluded") return "exclude";
  return decision;
}

function decisionLabel(decision: ReviewDecision): string {
  if (decision === "include") return "included";
  if (decision === "exclude") return "excluded";
  return "maybe";
}

function decisionToneClass(decision: ReviewDecision): string {
  if (decision === "include") return "text-[#218358]";
  if (decision === "exclude") return "text-[#CE2C31]";
  return "text-[#646464]";
}

function formatRelativeReviewTime(dateStr: string, nowMs = Date.now()): string {
  const date = new Date(dateStr);
  const diffMs = nowMs - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const searchSchema = z.object({
  citationId: z.string().optional(),
  panelTab: z.enum(["queue", "notes", "details"]).optional(),
});

export const Route = createFileRoute(
  "/_auth/projects/$projectId/screening/title-abstract/review",
)({
  validateSearch: searchSchema,
  loader: async ({ context, params, location, preload }) => {
    const settings = await context.queryClient.ensureQueryData(
      screeningSettingsQueryOptions(params.projectId),
    );

    const isDualMode = settings.screeningMode === "dual";

    const [singleQueue, dualQueue] = await Promise.all([
      context.queryClient.ensureQueryData(screeningQueueQueryOptions(params.projectId)),
      context.queryClient.ensureQueryData(
        dualScreeningQueueQueryOptions(params.projectId),
      ),
      context.queryClient.ensureQueryData(
        screeningReviewedHistoryQueryOptions(params.projectId),
      ),
      context.queryClient.ensureQueryData(
        dualScreeningReviewedHistoryQueryOptions(params.projectId),
      ),
      context.queryClient.ensureQueryData(screeningStatsQueryOptions(params.projectId)),
      context.queryClient.ensureQueryData(
        dualScreeningStatsQueryOptions(params.projectId),
      ),
      context.queryClient.ensureQueryData(exclusionCodesQueryOptions(params.projectId)),
    ]);

    // Prefetch project-level tag data (non-blocking so navigation stays fast)
    void context.queryClient.prefetchQuery(projectTagsQueryOptions(params.projectId));
    void context.queryClient.prefetchQuery(suggestedTagsQueryOptions(params.projectId));

    // Redirect to include citationId in URL if not present (skip during preload)
    const search = location.search as {
      citationId?: string;
      panelTab?: ScreeningWorkbenchTab;
    };
    if (!preload && !search.citationId) {
      const queue = isDualMode ? dualQueue : singleQueue;
      const firstCitation = queue?.[0];
      if (firstCitation) {
        throw redirect({
          to: "/projects/$projectId/screening/title-abstract/review",
          params: { projectId: params.projectId },
          search: { ...search, citationId: firstCitation.id },
          replace: true,
        });
      }
    }

    // Prefetch citation-level data (non-blocking)
    if (search.citationId) {
      void context.queryClient.prefetchQuery(
        citationTagsQueryOptions(params.projectId, search.citationId),
      );
      void context.queryClient.prefetchQuery(
        citationNotesQueryOptions(params.projectId, search.citationId),
      );
    }

    return { isDualMode };
  },
  component: ScreeningReviewPage,
});

function ScreeningReviewPage() {
  const { projectId, projectTitle, userId } =
    useProjectContext();
  const { citationId: searchCitationId, panelTab: searchPanelTab } = Route.useSearch();
  const { isDualMode } = Route.useLoaderData();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
  const [mobileWorkbenchOpen, setMobileWorkbenchOpen] = useState(false);
  const [focusCurrentTrigger, setFocusCurrentTrigger] = useState<number | undefined>(
    undefined,
  );
  const [isChangingReviewedDecision, setIsChangingReviewedDecision] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const undoStackRef = useRef(undoStack);
  undoStackRef.current = undoStack;

  const [exclusionDialogOpen, setExclusionDialogOpen] = useState(false);

  const getCachedUserTags = useCallback(
    (citationId: string): string[] => {
      const citationTags =
        queryClient.getQueryData<CitationTag[]>(
          citationTagsQueryOptions(projectId, citationId).queryKey,
        ) ?? [];

      return citationTags
        .filter((tag) => tag.createdBy === userId)
        .map((tag) => tag.name)
        .slice(0, 3);
    },
    [projectId, queryClient, userId],
  );

  const upsertReviewedCitation = useCallback(
    (citation: QueueCitation, decision: ReviewDecision, queuePosition?: number) => {
      const tagNames = getCachedUserTags(citation.id);
      setLocalReviewedCitations((prev) => {
        const filtered = prev.filter((entry) => entry.citation.id !== citation.id);
        const existing = prev.find((entry) => entry.citation.id === citation.id);
        return [
          ...filtered,
          {
            citation,
            decision,
            reviewedAt: new Date().toISOString(),
            tagNames,
            queuePosition: queuePosition ?? existing?.queuePosition,
          },
        ].slice(-MAX_RECENT_REVIEWED);
      });
    },
    [getCachedUserTags],
  );

  const { data: singleQueue } = useSuspenseQuery(screeningQueueQueryOptions(projectId));
  const { data: dualQueue } = useSuspenseQuery(dualScreeningQueueQueryOptions(projectId));
  const { data: singleReviewedHistory } = useSuspenseQuery(
    screeningReviewedHistoryQueryOptions(projectId),
  );
  const { data: dualReviewedHistory } = useSuspenseQuery(
    dualScreeningReviewedHistoryQueryOptions(projectId),
  );
  const { data: singleStats } = useSuspenseQuery(screeningStatsQueryOptions(projectId));
  const { data: dualStats } = useSuspenseQuery(dualScreeningStatsQueryOptions(projectId));

  const queue = isDualMode ? (dualQueue ?? []) : (singleQueue ?? []);
  const [localReviewedCitations, setLocalReviewedCitations] = useState<
    ReviewedCitationItem[]
  >([]);
  const persistedReviewedCitations = (
    isDualMode ? dualReviewedHistory : singleReviewedHistory
  ) as ReviewedCitationItem[];
  const reviewedCitations = useMemo(() => {
    const merged = new Map<string, ReviewedCitationItem>();
    for (const entry of persistedReviewedCitations) {
      merged.set(entry.citation.id, entry);
    }
    for (const entry of localReviewedCitations) {
      merged.set(entry.citation.id, entry);
    }

    return [...merged.values()]
      .sort((a, b) => new Date(a.reviewedAt).getTime() - new Date(b.reviewedAt).getTime())
      .slice(-MAX_RECENT_REVIEWED);
  }, [localReviewedCitations, persistedReviewedCitations]);

  const stats = isDualMode
    ? {
        total: dualStats?.total ?? 0,
        pending: (dualStats?.pending ?? 0) + (dualStats?.awaitingSecond ?? 0),
        included: dualStats?.agreedInclude ?? 0,
        excluded: dualStats?.agreedExclude ?? 0,
        maybe: dualStats?.agreedMaybe ?? 0,
        awaitingSecond: dualStats?.awaitingSecond ?? 0,
        conflict: dualStats?.conflict ?? 0,
      }
    : {
        total: singleStats?.total ?? 0,
        pending: singleStats?.pending ?? 0,
        included: singleStats?.included ?? 0,
        excluded: singleStats?.excluded ?? 0,
        maybe: singleStats?.maybe ?? 0,
      };
  const currentCitation = useMemo(() => {
    if (searchCitationId) {
      const fromUrl = queue.find((c) => c.id === searchCitationId);
      if (fromUrl) return fromUrl;
      const reviewedFromUrl = reviewedCitations.find(
        (entry) => entry.citation.id === searchCitationId,
      );
      if (reviewedFromUrl) return reviewedFromUrl.citation;
    }
    return queue[0] ?? reviewedCitations[reviewedCitations.length - 1]?.citation;
  }, [queue, reviewedCitations, searchCitationId]);
  const currentCitationId = currentCitation?.id;
  const panelTab = searchPanelTab ?? "queue";
  const currentReviewedEntry = useMemo(
    () => reviewedCitations.find((entry) => entry.citation.id === currentCitationId),
    [currentCitationId, reviewedCitations],
  );
  const showDecisionActions = !currentReviewedEntry || isChangingReviewedDecision;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setIsChangingReviewedDecision(false);
  }, [currentCitationId]);

  const goToNextCitation = useCallback(
    (nextCitationId?: string) => {
      navigate({
        to: "/projects/$projectId/screening/title-abstract/review",
        params: { projectId },
        search: (prev) =>
          nextCitationId
            ? { ...prev, citationId: nextCitationId }
            : { ...prev, citationId: undefined },
        replace: true,
      });
    },
    [navigate, projectId],
  );

  const goToCitation = useCallback(
    (citationId: string) => {
      navigate({
        to: "/projects/$projectId/screening/title-abstract/review",
        params: { projectId },
        search: (prev) => ({ ...prev, citationId }),
        replace: true,
      });
    },
    [navigate, projectId],
  );

  const setPanelTab = useCallback(
    (tab: ScreeningWorkbenchTab) => {
      navigate({
        to: "/projects/$projectId/screening/title-abstract/review",
        params: { projectId },
        search: (prev) => ({ ...prev, panelTab: tab }),
        replace: true,
      });
    },
    [navigate, projectId],
  );

  const focusCurrentInQueue = useCallback(() => {
    setFocusCurrentTrigger((prev) => (prev === undefined ? 1 : prev + 1));
  }, []);

  const singleDecisionMutation = useMutation({
    mutationFn: (params: {
      citationId: string;
      decision: "included" | "excluded" | "maybe";
      exclusionCode?: string;
      exclusionNotes?: string;
    }) =>
      $submitScreeningDecision({
        data: {
          projectId,
          citationId: params.citationId,
          decision: params.decision,
          exclusionCode: params.exclusionCode,
          exclusionNotes: params.exclusionNotes,
        },
      }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["projects", projectId, "screening"] });

      const previousQueue = queryClient.getQueryData<ScreeningQueueCitation[]>(
        screeningQueueQueryOptions(projectId).queryKey,
      );
      const previousStats = queryClient.getQueryData<typeof singleStats>(
        screeningStatsQueryOptions(projectId).queryKey,
      );
      const previousReviewed = [...localReviewedCitations];
      const reviewedEntry = reviewedCitations.find(
        (entry) => entry.citation.id === variables.citationId,
      );

      const citationFromQueue = previousQueue?.find((c) => c.id === variables.citationId);
      const isPendingQueueCitation = Boolean(citationFromQueue);
      const currentIndex =
        previousQueue?.findIndex((c) => c.id === variables.citationId) ?? -1;
      const citationForRecent =
        citationFromQueue ??
        reviewedEntry?.citation ??
        (currentCitation?.id === variables.citationId ? currentCitation : undefined);
      const queuePosition =
        citationForRecent?.screeningOrdinal ?? reviewedEntry?.queuePosition;

      if (citationForRecent) {
        upsertReviewedCitation(
          citationForRecent,
          normalizeDecision(variables.decision),
          queuePosition,
        );
      }

      if (citationFromQueue) {
        setUndoStack((prev) => {
          const newStack = [
            { citation: citationFromQueue, decision: variables.decision },
            ...prev,
          ].slice(0, MAX_UNDO_STACK);
          return newStack;
        });
      }

      // Find the next citation before filtering
      const nextCitation = previousQueue?.[currentIndex + 1];

      if (previousQueue && isPendingQueueCitation) {
        queryClient.setQueryData(
          screeningQueueQueryOptions(projectId).queryKey,
          previousQueue.filter((c) => c.id !== variables.citationId),
        );
      }

      if (previousStats) {
        const newStats = { ...previousStats };
        const nextDecision = normalizeDecision(variables.decision);

        if (isPendingQueueCitation) {
          newStats.pending = Math.max(0, newStats.pending - 1);
        } else if (reviewedEntry) {
          if (reviewedEntry.decision === "include") {
            newStats.included = Math.max(0, newStats.included - 1);
          } else if (reviewedEntry.decision === "exclude") {
            newStats.excluded = Math.max(0, newStats.excluded - 1);
          } else {
            newStats.maybe = Math.max(0, newStats.maybe - 1);
          }
        }

        if (nextDecision === "include") {
          newStats.included++;
        } else if (nextDecision === "exclude") {
          newStats.excluded++;
        } else {
          newStats.maybe++;
        }
        queryClient.setQueryData(
          screeningStatsQueryOptions(projectId).queryKey,
          newStats,
        );
      }

      if (isPendingQueueCitation) {
        goToNextCitation(nextCitation?.id);
      }

      return { previousQueue, previousStats, previousReviewed };
    },
    onError: (err, variables, context) => {
      console.error("[ScreeningReview] Decision error:", err);
      if (context?.previousQueue) {
        queryClient.setQueryData(
          screeningQueueQueryOptions(projectId).queryKey,
          context.previousQueue,
        );
      }
      if (context?.previousStats) {
        queryClient.setQueryData(
          screeningStatsQueryOptions(projectId).queryKey,
          context.previousStats,
        );
      }
      if (context?.previousReviewed) {
        setLocalReviewedCitations(context.previousReviewed);
      }
      setUndoStack((prev) => prev.filter((e) => e.citation.id !== variables.citationId));
      goToCitation(variables.citationId);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "screening"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "citations"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "pipeline"],
        refetchType: "none",
      });
    },
  });

  const dualDecisionMutation = useMutation({
    mutationFn: (params: {
      citationId: string;
      decision: "include" | "exclude" | "maybe";
      exclusionCode?: string;
      exclusionNotes?: string;
    }) =>
      $submitDualScreeningDecision({
        data: {
          projectId,
          citationId: params.citationId,
          decision: params.decision,
          exclusionCode: params.exclusionCode,
          exclusionNotes: params.exclusionNotes,
        },
      }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: ["projects", projectId, "dual-screening"],
      });

      const previousQueue = queryClient.getQueryData<DualScreeningQueueCitation[]>(
        dualScreeningQueueQueryOptions(projectId).queryKey,
      );
      const previousStats = queryClient.getQueryData<typeof dualStats>(
        dualScreeningStatsQueryOptions(projectId).queryKey,
      );
      const previousReviewed = [...localReviewedCitations];
      const reviewedEntry = reviewedCitations.find(
        (entry) => entry.citation.id === variables.citationId,
      );

      const citationFromQueue = previousQueue?.find((c) => c.id === variables.citationId);
      const isPendingQueueCitation = Boolean(citationFromQueue);
      const currentIndex =
        previousQueue?.findIndex((c) => c.id === variables.citationId) ?? -1;
      const citationForRecent =
        citationFromQueue ??
        reviewedEntry?.citation ??
        (currentCitation?.id === variables.citationId ? currentCitation : undefined);
      const queuePosition =
        citationForRecent?.screeningOrdinal ?? reviewedEntry?.queuePosition;

      if (citationForRecent) {
        upsertReviewedCitation(
          citationForRecent,
          normalizeDecision(variables.decision),
          queuePosition,
        );
      }

      if (citationFromQueue) {
        setUndoStack((prev) => {
          const newStack = [
            { citation: citationFromQueue, decision: variables.decision },
            ...prev,
          ].slice(0, MAX_UNDO_STACK);
          return newStack;
        });
      }

      // Find the next citation before filtering
      const nextCitation = previousQueue?.[currentIndex + 1];

      if (previousQueue && isPendingQueueCitation) {
        queryClient.setQueryData(
          dualScreeningQueueQueryOptions(projectId).queryKey,
          previousQueue.filter((c) => c.id !== variables.citationId),
        );
      }

      // Optimistically update dual screening stats
      if (previousStats && isPendingQueueCitation) {
        const newStats = { ...previousStats };
        // In dual mode, the citation moves to "awaitingSecond" after first review
        // or resolves if this is the second review. For optimistic UI, we decrement
        // the queue-relevant counts
        if (newStats.pending !== undefined && newStats.pending > 0) {
          newStats.pending = newStats.pending - 1;
        }
        if (newStats.awaitingSecond !== undefined) {
          newStats.awaitingSecond = newStats.awaitingSecond + 1;
        }
        queryClient.setQueryData(
          dualScreeningStatsQueryOptions(projectId).queryKey,
          newStats,
        );
      }

      if (isPendingQueueCitation) {
        goToNextCitation(nextCitation?.id);
      }
      return { previousQueue, previousStats, previousReviewed };
    },
    onError: (err, variables, context) => {
      console.error("[ScreeningReview] Dual decision error:", err);
      if (context?.previousQueue) {
        queryClient.setQueryData(
          dualScreeningQueueQueryOptions(projectId).queryKey,
          context.previousQueue,
        );
      }
      if (context?.previousStats) {
        queryClient.setQueryData(
          dualScreeningStatsQueryOptions(projectId).queryKey,
          context.previousStats,
        );
      }
      if (context?.previousReviewed) {
        setLocalReviewedCitations(context.previousReviewed);
      }
      setUndoStack((prev) => prev.filter((e) => e.citation.id !== variables.citationId));
      goToCitation(variables.citationId);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "dual-screening"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "screening"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "statistics"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "citations"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "pipeline"],
        refetchType: "none",
      });
    },
  });

  const singleUndoMutation = useMutation({
    mutationFn: (citationId: string) =>
      $undoScreeningDecision({
        data: { projectId, citationId },
      }),
    onMutate: async (citationId) => {
      await queryClient.cancelQueries({ queryKey: ["projects", projectId, "screening"] });

      const undoEntry = undoStackRef.current.find((e) => e.citation.id === citationId);
      if (!undoEntry) return;

      const previousQueue = queryClient.getQueryData<ScreeningQueueCitation[]>(
        screeningQueueQueryOptions(projectId).queryKey,
      );
      const previousStats = queryClient.getQueryData<typeof singleStats>(
        screeningStatsQueryOptions(projectId).queryKey,
      );
      const previousUndoStack = [...undoStackRef.current];
      const previousReviewed = [...localReviewedCitations];

      setUndoStack((prev) => prev.filter((e) => e.citation.id !== citationId));
      setLocalReviewedCitations((prev) =>
        prev.filter((entry) => entry.citation.id !== citationId),
      );

      if (previousQueue) {
        const restoredCitation = {
          ...undoEntry.citation,
          screeningStatus: "pending" as const,
        } as ScreeningQueueCitation;
        queryClient.setQueryData(screeningQueueQueryOptions(projectId).queryKey, [
          restoredCitation,
          ...previousQueue,
        ]);
      }

      if (previousStats) {
        const newStats = { ...previousStats };
        newStats.pending++;
        const decision = undoEntry.decision;
        if (decision === "included") {
          newStats.included = Math.max(0, newStats.included - 1);
        } else if (decision === "excluded") {
          newStats.excluded = Math.max(0, newStats.excluded - 1);
        } else if (decision === "maybe") {
          newStats.maybe = Math.max(0, newStats.maybe - 1);
        }
        queryClient.setQueryData(
          screeningStatsQueryOptions(projectId).queryKey,
          newStats,
        );
      }

      goToCitation(citationId);
      return {
        previousQueue,
        previousStats,
        previousUndoStack,
        previousReviewed,
        citationId,
      };
    },
    onError: (err, citationId, context) => {
      console.error("[ScreeningReview] Undo error:", err);
      if (context?.previousQueue) {
        queryClient.setQueryData(
          screeningQueueQueryOptions(projectId).queryKey,
          context.previousQueue,
        );
      }
      if (context?.previousStats) {
        queryClient.setQueryData(
          screeningStatsQueryOptions(projectId).queryKey,
          context.previousStats,
        );
      }
      if (context?.previousUndoStack) {
        setUndoStack(context.previousUndoStack);
      }
      if (context?.previousReviewed) {
        setLocalReviewedCitations(context.previousReviewed);
      }
      goToNextCitation();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "screening"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "citations"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "pipeline"],
        refetchType: "none",
      });
    },
  });

  const dualUndoMutation = useMutation({
    mutationFn: (citationId: string) =>
      $undoDualScreeningDecision({
        data: { projectId, citationId },
      }),
    onMutate: async (citationId) => {
      await queryClient.cancelQueries({
        queryKey: ["projects", projectId, "dual-screening"],
      });

      const undoEntry = undoStackRef.current.find((e) => e.citation.id === citationId);
      if (!undoEntry) return;

      const previousQueue = queryClient.getQueryData<DualScreeningQueueCitation[]>(
        dualScreeningQueueQueryOptions(projectId).queryKey,
      );
      const previousStats = queryClient.getQueryData<typeof dualStats>(
        dualScreeningStatsQueryOptions(projectId).queryKey,
      );
      const previousUndoStack = [...undoStackRef.current];
      const previousReviewed = [...localReviewedCitations];

      setUndoStack((prev) => prev.filter((e) => e.citation.id !== citationId));
      setLocalReviewedCitations((prev) =>
        prev.filter((entry) => entry.citation.id !== citationId),
      );

      if (previousQueue) {
        const restoredCitation = undoEntry.citation as DualScreeningQueueCitation;
        queryClient.setQueryData(dualScreeningQueueQueryOptions(projectId).queryKey, [
          restoredCitation,
          ...previousQueue,
        ]);
      }

      // Optimistically update dual screening stats (reverse of decision)
      if (previousStats) {
        const newStats = { ...previousStats };
        if (newStats.pending !== undefined) {
          newStats.pending = newStats.pending + 1;
        }
        if (newStats.awaitingSecond !== undefined && newStats.awaitingSecond > 0) {
          newStats.awaitingSecond = newStats.awaitingSecond - 1;
        }
        queryClient.setQueryData(
          dualScreeningStatsQueryOptions(projectId).queryKey,
          newStats,
        );
      }

      goToCitation(citationId);
      return {
        previousQueue,
        previousStats,
        previousUndoStack,
        previousReviewed,
        citationId,
      };
    },
    onError: (err, citationId, context) => {
      console.error("[ScreeningReview] Dual undo error:", err);
      if (context?.previousQueue) {
        queryClient.setQueryData(
          dualScreeningQueueQueryOptions(projectId).queryKey,
          context.previousQueue,
        );
      }
      if (context?.previousStats) {
        queryClient.setQueryData(
          dualScreeningStatsQueryOptions(projectId).queryKey,
          context.previousStats,
        );
      }
      if (context?.previousUndoStack) {
        setUndoStack(context.previousUndoStack);
      }
      if (context?.previousReviewed) {
        setLocalReviewedCitations(context.previousReviewed);
      }
      goToNextCitation();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "dual-screening"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "screening"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "statistics"],
        refetchType: "none",
      });
    },
  });

  const isPending = isDualMode
    ? dualDecisionMutation.isPending || dualUndoMutation.isPending
    : singleDecisionMutation.isPending || singleUndoMutation.isPending;

  const handleInclude = useCallback(() => {
    if (currentCitationId && !isPending) {
      focusCurrentInQueue();
      setIsChangingReviewedDecision(false);
      if (isDualMode) {
        dualDecisionMutation.mutate({
          citationId: currentCitationId,
          decision: "include",
        });
      } else {
        singleDecisionMutation.mutate({
          citationId: currentCitationId,
          decision: "included",
        });
      }
    }
  }, [
    currentCitationId,
    isPending,
    isDualMode,
    focusCurrentInQueue,
    dualDecisionMutation,
    singleDecisionMutation,
  ]);

  const handleExclude = useCallback(() => {
    if (currentCitationId && !isPending) {
      setExclusionDialogOpen(true);
    }
  }, [currentCitationId, isPending]);

  const handleExclusionSubmit = useCallback(
    (exclusionCode: string, notes?: string) => {
      if (currentCitationId && !isPending) {
        focusCurrentInQueue();
        setIsChangingReviewedDecision(false);
        if (isDualMode) {
          dualDecisionMutation.mutate({
            citationId: currentCitationId,
            decision: "exclude",
            exclusionCode,
            exclusionNotes: notes,
          });
        } else {
          singleDecisionMutation.mutate({
            citationId: currentCitationId,
            decision: "excluded",
            exclusionCode,
            exclusionNotes: notes,
          });
        }
        setExclusionDialogOpen(false);
      }
    },
    [
      currentCitationId,
      isPending,
      isDualMode,
      focusCurrentInQueue,
      dualDecisionMutation,
      singleDecisionMutation,
    ],
  );

  const handleMaybe = useCallback(() => {
    if (currentCitationId && !isPending) {
      focusCurrentInQueue();
      setIsChangingReviewedDecision(false);
      if (isDualMode) {
        dualDecisionMutation.mutate({ citationId: currentCitationId, decision: "maybe" });
      } else {
        singleDecisionMutation.mutate({
          citationId: currentCitationId,
          decision: "maybe",
        });
      }
    }
  }, [
    currentCitationId,
    isPending,
    isDualMode,
    focusCurrentInQueue,
    dualDecisionMutation,
    singleDecisionMutation,
  ]);

  const handleUndo = useCallback(() => {
    if (undoStack.length > 0 && !isPending) {
      const lastEntry = undoStack[0];
      if (isDualMode) {
        dualUndoMutation.mutate(lastEntry.citation.id);
      } else {
        singleUndoMutation.mutate(lastEntry.citation.id);
      }
    }
  }, [undoStack, isPending, isDualMode, dualUndoMutation, singleUndoMutation]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Shortcuts only work outside of text inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        handleUndo();
        return;
      }

      if (!showDecisionActions) {
        return;
      }

      if ((e.key === "y" || e.key === "Y") && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleInclude();
        return;
      }

      if ((e.key === "n" || e.key === "N") && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleExclude();
        return;
      }

      if ((e.key === "m" || e.key === "M") && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleMaybe();
        return;
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleInclude, handleExclude, handleMaybe, handleUndo, showDecisionActions]);

  if (queue.length === 0 && reviewedCitations.length === 0) {
    return (
      <div className="flex min-h-screen flex-col">
        <main className="flex-1 px-6 py-10">
          <ScreeningEmptyState
            title="Nothing to screen right now"
            description={
              isDualMode
                ? "You have reviewed all currently assigned citations. More may appear as your team continues screening."
                : "All title and abstract citations have been screened."
            }
            actions={
              <Button render={<Link to="/projects/$projectId" params={{ projectId }} />}>
                Return to Project
              </Button>
            }
          />
        </main>
      </div>
    );
  }

  if (!currentCitation) {
    return (
      <div className="flex min-h-screen flex-col">
        <main className="flex-1 px-6 py-10">
          {isPending ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          ) : (
            <ScreeningEmptyState
              title="Nothing to screen right now"
              description="No citation is currently selected for review."
              actions={
                <Button render={<Link to="/projects/$projectId" params={{ projectId }} />}>
                  Return to Project
                </Button>
              }
            />
          )}
        </main>
      </div>
    );
  }

  const citationForCard: ScreeningQueueCitation = {
    id: currentCitation.id,
    screeningOrdinal: currentCitation.screeningOrdinal,
    title: currentCitation.title,
    authors: currentCitation.authors,
    year: currentCitation.year,
    containerTitle: currentCitation.containerTitle,
    doi: currentCitation.doi,
    pmid: currentCitation.pmid,
    abstract: currentCitation.abstract,
    volume: currentCitation.volume,
    issue: currentCitation.issue,
    pages: currentCitation.pages,
    screeningStatus: "pending",
  };

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden">
      <ScreeningHeader
        remaining={queue.length}
        isDualMode={isDualMode}
        actions={
          <Button
            variant="outline"
            size="sm"
            className="lg:hidden"
            onClick={() => setMobileWorkbenchOpen(true)}
          >
            <PanelRightOpen />
            Workbench
          </Button>
        }
      />

      {/* Two-column layout */}
      <div className="flex min-h-0 flex-1">
        {/* Main content column */}
        <div className="flex min-h-0 flex-1 flex-col">
          <ScrollArea className="min-h-0 flex-1 overflow-hidden">
            <main className="px-6 py-8">
              <div key={currentCitationId} className="animate-fade-in-up">
                <ScreeningCitationCard citation={citationForCard} />
              </div>
            </main>
          </ScrollArea>
          <TagNotesStrip
            key={currentCitationId}
            projectId={projectId}
            citationId={currentCitationId}
          />

          <div className="bg-background shrink-0 border-t p-4">
            <div className="mx-auto max-w-2xl">
              {showDecisionActions ? (
                <ScreeningActions
                  onInclude={handleInclude}
                  onExclude={handleExclude}
                  onMaybe={handleMaybe}
                  onUndo={handleUndo}
                  canUndo={undoStack.length > 0}
                  isLoading={isPending}
                />
              ) : currentReviewedEntry ? (
                <div className="bg-muted/50 border-border flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                  <p className="text-muted-foreground text-sm">
                    Previously marked{" "}
                    <span
                      className={`font-medium ${decisionToneClass(currentReviewedEntry.decision)}`}
                    >
                      {decisionLabel(currentReviewedEntry.decision)}
                    </span>{" "}
                    {formatRelativeReviewTime(currentReviewedEntry.reviewedAt, nowMs)}.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsChangingReviewedDecision(true)}
                    disabled={isPending}
                  >
                    Change decision
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Right sidebar */}
        <ScreeningSidebar
          projectId={projectId}
          citation={currentCitation}
          queue={queue}
          reviewed={reviewedCitations}
          focusCurrentTrigger={focusCurrentTrigger}
          currentCitationId={currentCitationId}
          onSelectCitation={goToCitation}
          panelTab={panelTab}
          onPanelTabChange={setPanelTab}
          open={rightSidebarOpen}
          onToggle={() => setRightSidebarOpen((prev) => !prev)}
        />
      </div>

      <Suspense fallback={null}>
        <ExclusionReasonDialog
          projectId={projectId}
          open={exclusionDialogOpen}
          onOpenChange={setExclusionDialogOpen}
          onSubmit={handleExclusionSubmit}
          isLoading={isPending}
        />
      </Suspense>

      <Sheet open={mobileWorkbenchOpen} onOpenChange={setMobileWorkbenchOpen}>
        <SheetContent side="right" className="gap-0 p-0">
          <SheetHeader className="border-b">
            <SheetTitle>Screening Workbench</SheetTitle>
          </SheetHeader>
          <div className="min-h-0 flex-1">
            <ScreeningSidebar
              mode="sheet"
              projectId={projectId}
              citation={currentCitation}
              queue={queue}
              reviewed={reviewedCitations}
              focusCurrentTrigger={focusCurrentTrigger}
              currentCitationId={currentCitationId}
              onSelectCitation={goToCitation}
              panelTab={panelTab}
              onPanelTabChange={setPanelTab}
              onQueueItemSelected={() => setMobileWorkbenchOpen(false)}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
